import { useCallback, useEffect, useRef, useState } from 'react';
import { chatMessageDomId } from '../helpers/chatMessage';

/** How many older pages to pull in while hunting for the quoted message. */
const MAX_PAGES_WALKED_BACK = 10;
/** A target taller than the viewport never reaches the ratio below. */
const GLARE_FALLBACK_MS = 900;
/** Long enough for the sweep to play; unmounting it lets the next jump replay. */
const GLARE_DURATION_MS = 1000;
/** How much of the message has to be on screen before the glare is worth it. */
const VISIBLE_ENOUGH = 0.6;

type Paging = {
  hasNextPage: boolean;
  fetchNextPage: () => Promise<unknown>;
};

/**
 * Jumping from a reply back to the message it quotes.
 *
 * Scrolls the target into view and plays a one-shot glare, mirroring the Menu
 * AI "focus item" behaviour. The target may be older than anything loaded, so
 * older pages are pulled in until it appears - and the glare waits for the
 * message to actually be on screen, since `scrollIntoView` is async and glaring
 * right after it would play off-screen.
 *
 * Returns the id currently glaring, for the bubble to render, and the function
 * a reply strip calls.
 */
export const useMessageFocus = (paging: Paging) => {
  const [highlightMessageId, setHighlightMessageId] = useState<number | null>(
    null
  );
  const cancelRef = useRef<(() => void) | null>(null);

  /**
   * Paging is read through a ref: `focusMessage` awaits between fetches, so a
   * value captured at click time would be stale by the second iteration.
   */
  const pagingRef = useRef(paging);
  useEffect(() => {
    pagingRef.current = paging;
  }, [paging]);

  const focusMessage = useCallback(async (messageId: number) => {
    cancelRef.current?.();
    // Clear first, so re-clicking the same reply restarts the mount-once glare.
    setHighlightMessageId(null);

    let cancelled = false;
    let observer: IntersectionObserver | null = null;
    const timers: number[] = [];
    const cleanup = () => {
      cancelled = true;
      observer?.disconnect();
      timers.forEach((timer) => window.clearTimeout(timer));
    };
    cancelRef.current = cleanup;

    const glare = () => {
      if (cancelled) return;
      cleanup();
      setHighlightMessageId(messageId);
    };

    const find = () => document.getElementById(chatMessageDomId(messageId));

    let element = find();
    // Cap the walk back: a reply to something thousands of messages ago is not
    // worth freezing the thread over.
    for (let page = 0; !element && page < MAX_PAGES_WALKED_BACK; page += 1) {
      if (!pagingRef.current.hasNextPage) break;
      await pagingRef.current.fetchNextPage();
      if (cancelled) return;
      // Let the prepended page commit before looking for the node.
      await new Promise((resolve) => window.setTimeout(resolve, 0));
      if (cancelled) return;
      element = find();
    }

    if (!element) {
      cleanup();
      return;
    }

    element.scrollIntoView({ behavior: 'smooth', block: 'center' });

    if (!('IntersectionObserver' in window)) return glare();

    observer = new IntersectionObserver(
      (entries) => {
        if (
          entries.some(
            (entry) =>
              entry.isIntersecting && entry.intersectionRatio >= VISIBLE_ENOUGH
          )
        ) {
          glare();
        }
      },
      { threshold: [0, VISIBLE_ENOUGH, 1] }
    );
    observer.observe(element);
    timers.push(window.setTimeout(glare, GLARE_FALLBACK_MS));
  }, []);

  // Unmount the glare once it has played, so the next jump can replay it.
  useEffect(() => {
    if (highlightMessageId === null) return undefined;
    const timer = window.setTimeout(
      () => setHighlightMessageId(null),
      GLARE_DURATION_MS
    );
    return () => window.clearTimeout(timer);
  }, [highlightMessageId]);

  useEffect(() => () => cancelRef.current?.(), []);

  return { highlightMessageId, focusMessage };
};
